/**
 * Coordenadas a partir de solicitações de alerta (GET list_alert_request).
 * Prioridade: risk_detections (FIRMS) → parse de location (manual).
 *
 * Também valida/normaliza pares lat/lon para evitar envio de pontos
 * inválidos no e-mail (ex.: "-2.0, 37834.0" gerado por vírgula decimal BR).
 */

/** Latitude −90…90 e longitude −180…180. */
export function isValidLatLon(lat, lon) {
  if (typeof lat !== "number" || typeof lon !== "number") return false;
  if (Number.isNaN(lat) || Number.isNaN(lon)) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

/**
 * Interpreta um par de coordenadas em texto.
 * Aceita:
 * - "-2.37834, -57.23727" (ponto decimal)
 * - "-2,37834, -57,23727" (vírgula decimal BR)
 * - separadores `,` ou `;`
 *
 * @returns {{ lat: number, lon: number, formatted: string } | null}
 */
export function parseCoordinatePair(input) {
  if (input == null) return null;
  const t = String(input).trim();
  if (!t) return null;

  // Formato BR: -2,37834, -57,23727  (vírgula decimal + vírgula como separador)
  const br = t.match(/^(-?\d+),(\d+)\s*[,;]\s*(-?\d+),(\d+)$/);
  if (br) {
    const lat = Number(`${br[1]}.${br[2]}`);
    const lon = Number(`${br[3]}.${br[4]}`);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
    return { lat, lon, formatted: formatLatLon(lat, lon) };
  }

  // Formato EN / ponto decimal: -2.37834, -57.23727
  const en = t.match(/^(-?\d+(?:\.\d+)?)\s*[,;]\s*(-?\d+(?:\.\d+)?)$/);
  if (en) {
    const lat = Number(en[1]);
    const lon = Number(en[2]);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
    return { lat, lon, formatted: formatLatLon(lat, lon) };
  }

  return null;
}

export function parseLocationAsCoordinates(locationStr) {
  const parsed = parseCoordinatePair(locationStr);
  return parsed?.formatted || "";
}

/** Pontos FIRMS com lat/lon numéricos (ainda podem ser geograficamente inválidos). */
export function getRawRiskDetectionsFromAlertRequest(item) {
  if (!item?.risk_detections?.length) return [];
  return item.risk_detections.filter(
    (p) =>
      typeof p.lat === "number" &&
      typeof p.lon === "number" &&
      !Number.isNaN(p.lat) &&
      !Number.isNaN(p.lon)
  );
}

function distanceOf(point) {
  const d = point?.distance_km;
  if (d == null || d === "") return null;
  const n = Number(d);
  return Number.isFinite(n) ? n : null;
}

/** true quando o backend calculou a distância de algum foco até o KML da área. */
export function hasFocusDistance(points) {
  return Boolean(points?.some((p) => distanceOf(p) != null));
}

/**
 * Ordena do foco mais próximo ao mais distante do KML (sem distância → fim).
 * Ordenação estável: mantém a ordem original entre empates.
 */
export function sortFocusByDistance(points) {
  if (!points?.length) return [];
  return points
    .map((p, idx) => ({ p, idx, d: distanceOf(p) }))
    .sort((a, b) => {
      if (a.d == null && b.d == null) return a.idx - b.idx;
      if (a.d == null) return 1;
      if (b.d == null) return -1;
      return a.d - b.d || a.idx - b.idx;
    })
    .map(({ p }) => p);
}

function formatKm(d) {
  return `${d.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} km`;
}

/**
 * Texto de distância do foco — o mesmo do e-mail (`distance_label` da API).
 * Fallback local replica o texto do backend para respostas antigas.
 */
export function formatFocusDistance(point) {
  if (point?.distance_label) return point.distance_label;
  const d = distanceOf(point);
  if (d == null) return "";
  if (point.inside_area === true) return "DENTRO da propriedade (KML)";
  return `${formatKm(d)} da propriedade (KML)`;
}

/**
 * Mensagem PRIORIDADE — a mesma do e-mail (`focus_priority_message` da API).
 * Vazio quando não há distância calculada.
 */
export function getFocusPriorityMessage(item) {
  if (item?.focus_priority_message) return item.focus_priority_message;
  const nearest = getNearestFocusFromAlertRequest(item);
  if (!nearest) return "";
  const coords = formatLatLon(nearest.lat, nearest.lon);
  if (nearest.inside_area === true) {
    return `Foco DENTRO da propriedade indicada pelo KML: ${coords}`;
  }
  return `Foco mais próximo da propriedade: ${coords} — ${formatKm(
    distanceOf(nearest)
  )} do limite do KML`;
}

export const FOCUS_LIST_CAPTION =
  "Lista ordenada do foco mais próximo ao mais distante da propriedade (KML)";

/** Pontos FIRMS geograficamente válidos, do mais próximo ao mais distante do KML. */
export function getRiskDetectionsFromAlertRequest(item) {
  return sortFocusByDistance(
    getRawRiskDetectionsFromAlertRequest(item).filter((p) =>
      isValidLatLon(p.lat, p.lon)
    )
  );
}

/** Foco mais próximo do KML (ou dentro dele), quando houver distância calculada. */
export function getNearestFocusFromAlertRequest(item) {
  const points = getRiskDetectionsFromAlertRequest(item);
  return hasFocusDistance(points) ? points[0] : null;
}

/** Pontos FIRMS com lat/lon fora da faixa (quebram Google Maps / OSM). */
export function getInvalidRiskDetectionsFromAlertRequest(item) {
  return getRawRiskDetectionsFromAlertRequest(item).filter(
    (p) => !isValidLatLon(p.lat, p.lon)
  );
}

export function formatLatLon(lat, lon) {
  return `${lat}, ${lon}`;
}

/**
 * Valor para o campo "Coordenadas" do formulário de novo alerta.
 * - Focos com distância ao KML: foco mais próximo (ou dentro) da área
 * - 1 ponto FIRMS: lat, lon do foco
 * - Vários pontos sem distância: centroide (média lat/lon)
 * - Sem FIRMS: tenta location textual (ex. manual "-8.40, -53.58")
 */
export function getCoordinatesFromAlertRequest(item) {
  if (!item) return "";

  const points = getRiskDetectionsFromAlertRequest(item);
  if (points.length === 1 || hasFocusDistance(points)) {
    return formatLatLon(points[0].lat, points[0].lon);
  }
  if (points.length > 1) {
    const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const lon = points.reduce((s, p) => s + p.lon, 0) / points.length;
    return formatLatLon(Number(lat.toFixed(6)), Number(lon.toFixed(6)));
  }

  return parseLocationAsCoordinates(item.location) || "";
}

/** Alias — pontos plotáveis / enviáveis no POST /alerts/. */
export function getMapPoints(item) {
  return getRiskDetectionsFromAlertRequest(item);
}

/** Campos FIRMS enviados ao backend (sem undefined; só pontos válidos). */
export function serializeRiskDetectionsForCreate(points) {
  if (!points?.length) return null;
  const valid = points.filter((p) => isValidLatLon(p.lat, p.lon));
  if (!valid.length) return null;
  return valid.map((p) => {
    const out = { lat: p.lat, lon: p.lon };
    if (p.acq_date) out.acq_date = p.acq_date;
    if (p.acq_time) out.acq_time = p.acq_time;
    if (p.satellite) out.satellite = p.satellite;
    if (p.instrument) out.instrument = p.instrument;
    if (p.confidence) out.confidence = p.confidence;
    if (p.frp != null && !Number.isNaN(Number(p.frp))) out.frp = p.frp;
    if (p.brightness != null && !Number.isNaN(Number(p.brightness))) {
      out.brightness = p.brightness;
    }
    if (p.firms_query_source) out.firms_query_source = p.firms_query_source;
    if (distanceOf(p) != null) out.distance_km = distanceOf(p);
    if (typeof p.inside_area === "boolean") out.inside_area = p.inside_area;
    if (p.distance_label) out.distance_label = p.distance_label;
    return out;
  });
}

/**
 * Avalia coordenadas do formulário + focos FIRMS antes do POST.
 * Não bloqueia criação — só reporta problemas e normaliza o que for possível.
 *
 * @returns {{
 *   hasIssues: boolean,
 *   allValid: boolean,
 *   normalizedCoordinates: string,
 *   referenceValid: boolean | null,
 *   validFocusCount: number,
 *   invalidFocusCount: number,
 *   invalidFocusSamples: string[],
 *   message: string,
 * }}
 */
export function assessAlertCoordinates(coordinatesText, alertRequest = null) {
  const issues = [];
  let normalizedCoordinates = String(coordinatesText || "").trim();
  let referenceValid = null;

  const parsedRef = parseCoordinatePair(normalizedCoordinates);
  if (normalizedCoordinates) {
    if (!parsedRef) {
      referenceValid = false;
      issues.push(
        "A coordenada de referência no campo não pôde ser interpretada. Use o formato: latitude, longitude (ex.: -2.37834, -57.23727)."
      );
    } else if (!isValidLatLon(parsedRef.lat, parsedRef.lon)) {
      referenceValid = false;
      issues.push(
        `A coordenada de referência (${parsedRef.formatted}) está fora da faixa válida (lat −90…90, lon −180…180) e não abre no Maps.`
      );
      // Ainda normaliza o texto (ponto decimal) para o backend
      normalizedCoordinates = parsedRef.formatted;
    } else {
      referenceValid = true;
      normalizedCoordinates = parsedRef.formatted;
    }
  }

  const validFocus = getRiskDetectionsFromAlertRequest(alertRequest);
  const invalidFocus = getInvalidRiskDetectionsFromAlertRequest(alertRequest);
  const invalidFocusSamples = invalidFocus.slice(0, 5).map(
    (p) => `${p.lat}, ${p.lon}`
  );

  if (invalidFocus.length > 0) {
    issues.push(
      `${invalidFocus.length} ponto(s) de foco FIRMS inválido(s) serão omitidos do envio (ex.: ${invalidFocusSamples.join(
        " · "
      )}). Valores como longitude 37834 costumam nascer de vírgula decimal brasileira interpretada como separador.`
    );
  }

  const hasIssues = issues.length > 0;
  const allValid =
    !hasIssues &&
    (referenceValid === true || referenceValid === null) &&
    invalidFocus.length === 0;

  let message;
  if (hasIssues) {
    message = [
      "Encontramos problemas nas coordenadas antes de registrar o alerta.",
      "",
      ...issues.map((i, idx) => `${idx + 1}. ${i}`),
      "",
      "O alerta será criado mesmo assim. Revise o campo Coordenadas se o e-mail ao cliente precisar de um link Maps correto.",
    ].join("\n");
  } else {
    const priority = getFocusPriorityMessage(alertRequest);
    const nearestNote = priority ? ` PRIORIDADE — ${priority}.` : "";
    const focusNote =
      validFocus.length > 0
        ? ` ${validFocus.length} foco(s) FIRMS válido(s) serão incluídos no e-mail.${nearestNote}`
        : "";
    message = `Coordenadas conferidas: referência válida (${normalizedCoordinates}).${focusNote}`;
  }

  return {
    hasIssues,
    allValid,
    normalizedCoordinates,
    referenceValid,
    validFocusCount: validFocus.length,
    invalidFocusCount: invalidFocus.length,
    invalidFocusSamples,
    message,
  };
}

/**
 * Payload para POST /api/alerts/ (Opção B: risk_detections explícito quando houver focos FIRMS).
 * @param {object} formData — guardian_area, alert_type, severity_level, coordinates, observation
 * @param {{ alertRequest?: object|null, alertRequestId?: string }} [opts]
 */
export function buildCreateAlertPayload(formData, opts = {}) {
  const alertRequest = opts.alertRequest ?? null;
  const requestId = alertRequest?.id || opts.alertRequestId || null;

  const assessment = assessAlertCoordinates(formData.coordinates, alertRequest);

  const payload = {
    guardian_area: formData.guardian_area,
    alert_type: formData.alert_type,
    severity_level: formData.severity_level,
    coordinates: assessment.normalizedCoordinates || formData.coordinates,
    observation: formData.observation || "",
  };

  if (requestId) {
    payload.alert_request = requestId;
  }

  const serialized = serializeRiskDetectionsForCreate(getMapPoints(alertRequest));
  if (serialized?.length) {
    payload.risk_detections = serialized;
  }

  return payload;
}
